"use client";

import { useEffect } from "react";

const BUILD = process.env.NEXT_PUBLIC_BUILD_ID ?? "dev";

/** Borra caché del navegador y almacenamiento local (no toca la cookie de sesión). */
export async function clearAppCache() {
  try {
    if ("caches" in window) await Promise.all((await caches.keys()).map((k) => caches.delete(k)));
    // El service worker NO se desregistra: los avisos del teléfono (push) viven en él. Solo se le pide actualizarse.
    if (navigator.serviceWorker) await Promise.all((await navigator.serviceWorker.getRegistrations()).map((r) => r.update().catch(() => {})));
    sessionStorage.clear();
    localStorage.clear();
  } catch {}
}

const send = (data: Record<string, unknown>) => {
  try {
    navigator.sendBeacon("/api/client-log", JSON.stringify({ ...data, build: BUILD, path: location.pathname, standalone: !!(navigator as unknown as { standalone?: boolean }).standalone, ua: navigator.userAgent.slice(0, 90) }));
  } catch {}
};

/**
 * Vigila la app en el teléfono, donde nadie puede ver la consola:
 * - borra la caché sola cuando hay una versión nueva;
 * - avisa al servidor de errores de JavaScript (máx. 5 por visita);
 * - avisa cuando un toque tarda en cambiar de pantalla o se queda colgado.
 */
export function ClientErrors() {
  useEffect(() => {
    // Versión nueva → caché limpia (una sola vez por versión) y, después, se registra el service worker.
    let ready: Promise<void> = Promise.resolve();
    try {
      const prev = localStorage.getItem("ahro_build");
      if (prev !== BUILD) {
        ready = clearAppCache().then(() => {
          try { localStorage.setItem("ahro_build", BUILD); } catch {}
          if (prev) location.reload();
        });
      }
    } catch {}
    send({ kind: "carga" });
    // Estructura de la app guardada en el teléfono (abre al instante). Solo en la versión compilada.
    if (BUILD !== "dev" && "serviceWorker" in navigator) {
      ready.then(() => navigator.serviceWorker.register("/sw.js", { scope: "/" })).catch((e) => send({ kind: "sw-error", msg: String(e?.message ?? e) }));
    }

    let errors = 0;
    const report = (kind: string, msg: string) => { if (errors < 5) { errors++; send({ kind, msg: msg.slice(0, 400) }); } };
    const onErr = (e: ErrorEvent) => report("error", `${e.message} @${(e.filename || "").split("/").pop()}:${e.lineno}`);
    const onRej = (e: PromiseRejectionEvent) => report("promesa", String((e.reason && (e.reason.message || e.reason)) ?? "sin detalle"));
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);

    // toque en un enlace interno → ¿cuánto tarda en cambiar la pantalla?
    let pending: { path: string; t: number; warned: boolean } | null = null;
    const onClick = (e: MouseEvent) => {
      const a = (e.target as Element | null)?.closest?.("a[href^='/']") as HTMLAnchorElement | null;
      if (!a) return;
      const path = new URL(a.href).pathname;
      if (path !== location.pathname) pending = { path, t: performance.now(), warned: false };
    };
    document.addEventListener("click", onClick, true);
    const timer = setInterval(() => {
      if (!pending) return;
      const el = performance.now() - pending.t;
      if (location.pathname === pending.path) {
        if (el > 1200) send({ kind: "nav-lenta", msg: `${pending.path} tardó ${Math.round(el)} ms` });
        pending = null;
      } else if (el > 5000 && !pending.warned) {
        pending.warned = true;
        send({ kind: "nav-colgada", msg: `${pending.path} sin cambiar tras ${Math.round(el)} ms` });
      } else if (el > 30000) pending = null;
    }, 200);

    return () => { window.removeEventListener("error", onErr); window.removeEventListener("unhandledrejection", onRej); document.removeEventListener("click", onClick, true); clearInterval(timer); };
  }, []);
  return null;
}
